import { Box, IconButton, Popper, Tooltip, ClickAwayListener, useTheme, Typography, Checkbox } from "@mui/material"
import React, { useRef, useState } from "react"
import { observer } from "mobx-react"

import { ANNOTATION_COLORS, Icons } from "../../utils/virtualboard-utilities"
import { useVirtualboardStore } from "../../store/virtualboard.store"

const VirtualboardTools = observer( ( { onEvent } ) => {

    const [strokeOptionsOpen, setStrokeOptionsOpen] = useState( false )

    const { palette, border, shadows } = useTheme()

    const store = useVirtualboardStore()
    const paletteBtnRef = useRef( null )

    const getToogleBtnStyles = ( active = false ) => ( {
        aspectRatio: "1 / 1",
        borderRadius: "5px",
        fontSize: "18px",
        bgcolor: active ? palette.grey[600] + "44" : "",
        color: palette.common.font,
        // border: active ? `1px solid #fff0` : border[1],
        ":hover": { bgcolor: active ? palette.grey[600] + "44" : "" },
    } )

    const setTool = ( tool ) => {
        store.setBoardState( { selected: new Set() } )
        store.setTool( tool )
    }

    const clearCanvas = () => {
        store.clear()
    }

    const handleStrokeSettingChange = ( property, value ) => {

        const slectedElements = store.getBoardState?.selected
        if ( slectedElements?.size === 0 ) {
            store.updateBoardGlobalSettings( { [property]: value } )
        } else {
            const changes = {
                [property]: value
            }
            const elements = store.getElements
            for ( const elementId of slectedElements ) {
                const el = elements.find( e => e.id === elementId )

                if ( 'color' === property ) {
                    changes.colorInfo = value
                }
            }

            const updateEvent = store.updateElements( [...store.getBoardState.selected], changes )
            onEvent( updateEvent )
        }
    }


    return (
        <Box
            boxShadow={shadows[1]}
            bgcolor={palette.background.paper}
            zIndex={10}
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="5px"
            left="10px"
            top="50%"
            sx={{ transform: `translateY(-50%)` }}
            padding="10px 5px"
            borderRadius="20px"
            flexWrap="wrap"
        >
            {/* === Color & Stroke Popper === */}
            <Popper
                sx={{ zIndex: "10" }}
                anchorEl={paletteBtnRef.current}
                open={strokeOptionsOpen}
                placement="auto"
            >
                <ClickAwayListener
                    onClickAway={() => setStrokeOptionsOpen( false )}
                >
                    <Box bgcolor={palette.background.paper} borderRadius="10px" padding="10px" boxShadow={shadows[1]}>
                        {/* Colors */}
                        <Box display="grid" justifyContent="center" gridTemplateColumns="repeat(4,1fr)" gap="10px">
                            {ANNOTATION_COLORS.map( ( color ) => (
                                <Tooltip disableInteractive key={color.name} title={color.name}>
                                    <Box
                                        onClick={() => handleStrokeSettingChange( "color", color )}
                                        sx={{
                                            cursor: "pointer",
                                            borderColor:
                                                store.getBoardGlobalSettings.color.name === color.name
                                                    ? palette.common.font
                                                    : "",
                                            ":hover": { borderColor: palette.common.font },
                                        }}
                                        border={border[1]}
                                        bgcolor={`rgb(${color.r},${color.g},${color.b})`}
                                        width="20px"
                                        height="20px"
                                        borderRadius="50%"
                                    />
                                </Tooltip>
                            ) )}
                        </Box>

                        {/* Stroke & Fill */}
                        <Box
                            marginTop="10px"
                            padding="0 10px"
                            display="flex"
                            alignItems="center"
                            gap="5px"
                            borderTop={border[1]}
                        >
                            <Box display="flex" alignItems="center" width="100%" justifyContent="space-between" gap="10px">
                                <Typography variant='subtitle2'>Fill</Typography>
                                <Checkbox size='small' onChange={() => handleStrokeSettingChange( "fill", !store.getBoardGlobalSettings.fill )} checked={store.getBoardGlobalSettings.fill} />
                            </Box>
                        </Box>

                        {/* Line Styles */}
                        <Box
                            padding="10px"
                        >
                            <Typography fontSize="10px">STOKE STYLE</Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="5px"
                            >
                                <Tooltip placement="top" disableInteractive title="Solid">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.strokeType === "solid" )}
                                        onClick={() => handleStrokeSettingChange( "strokeType", "solid" )}
                                    >
                                        <Box sx={{ width: "15px", borderRadius: "50%", aspectRatio: "1 / 1", border: `2px solid ${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Dotted">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.strokeType === "dotted" )}
                                        onClick={() => handleStrokeSettingChange( "strokeType", "dotted" )}
                                    >
                                        <Box sx={{ width: "15px", borderRadius: "50%", aspectRatio: "1 / 1", border: `2px dotted ${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Dashed">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.strokeType === "dashed" )}
                                        onClick={() => handleStrokeSettingChange( "strokeType", "dashed" )}
                                    >
                                        <Box sx={{ width: "15px", borderRadius: "50%", aspectRatio: "1 / 1", border: `2px dashed ${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>


                        {/* Line Width */}
                        <Box
                            padding="2px 10px"
                        >
                            <Typography fontSize="10px">STOKE WIDTH</Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="5px"
                            >
                                <Tooltip placement="top" disableInteractive title="Small">
                                    <IconButton sx={{ ...getToogleBtnStyles( store.getBoardGlobalSettings.lineWidthScale === 1 ), width: "30px", aspectRatio: "1 / 1" }} onClick={() => handleStrokeSettingChange( "lineWidthScale", 1 )} >
                                        <Box sx={{ width: "5px", borderRadius: "50%", aspectRatio: "1 / 1", bgcolor: `${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Normal">
                                    <IconButton sx={{ ...getToogleBtnStyles( store.getBoardGlobalSettings.lineWidthScale === 2 ), width: "30px", aspectRatio: "1 / 1" }} onClick={() => handleStrokeSettingChange( "lineWidthScale", 2 )} >
                                        <Box sx={{ width: "7px", borderRadius: "50%", aspectRatio: "1 / 1", bgcolor: `${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Medium">
                                    <IconButton sx={{ ...getToogleBtnStyles( store.getBoardGlobalSettings.lineWidthScale === 4 ), width: "30px", aspectRatio: "1 / 1" }} onClick={() => handleStrokeSettingChange( "lineWidthScale", 4 )} >
                                        <Box sx={{ width: "9px", borderRadius: "50%", aspectRatio: "1 / 1", bgcolor: `${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Large">
                                    <IconButton sx={{ ...getToogleBtnStyles( store.getBoardGlobalSettings.lineWidthScale === 6 ), width: "30px", aspectRatio: "1 / 1" }} onClick={() => handleStrokeSettingChange( "lineWidthScale", 6 )} >
                                        <Box sx={{ width: "9px", borderRadius: "50%", aspectRatio: "1 / 1", bgcolor: `${palette.common.font}`, }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>



                        {/* Font Family */}
                        <Box
                            padding="10px"
                        >
                            <Typography fontSize="10px">FONT STYLE</Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="5px"
                            >
                                <Tooltip placement="top" disableInteractive title="Sans Serif">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontFamily === "Montserrat" )}
                                        onClick={() => handleStrokeSettingChange( "fontFamily", "Montserrat" )}
                                    >
                                        <Typography fontSize="18px" fontWeight="600" fontFamily="Montserrat" >Aa</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Sans Blobby">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontFamily === "Sour Gummy" )}
                                        onClick={() => handleStrokeSettingChange( "fontFamily", "Sour Gummy" )}
                                    >
                                        <Typography fontSize="18px" fontWeight="600" fontFamily="Sour Gummy" >Aa</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Calligraphy">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontFamily === "Ms Madi" )}
                                        onClick={() => handleStrokeSettingChange( "fontFamily", "Ms Madi" )}
                                    >
                                        <Typography fontSize="18px" fontWeight="600" fontFamily="Ms Madi" >Aa</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Handwritten">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontFamily === "Playwrite DE SAS" )}
                                        onClick={() => handleStrokeSettingChange( "fontFamily", "Playwrite DE SAS" )}
                                    >
                                        <Typography fontSize="18px" fontWeight="600" fontFamily="Playwrite DE SAS" >Aa</Typography>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>



                        {/* Font Family */}
                        <Box
                            padding="10px"
                        >
                            <Typography fontSize="10px">FONT SIZE</Typography>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="5px"
                            >
                                <Tooltip placement="top" disableInteractive title="Small">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontSize === 12 )}
                                        onClick={() => handleStrokeSettingChange( "fontSize", 12 )}
                                    >
                                        <Typography fontSize="18px" sx={{ aspectRatio: "1 / 1", width: "25px" }} fontWeight="800" >S</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Normal">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontSize === 16 )}
                                        onClick={() => handleStrokeSettingChange( "fontSize", 16 )}
                                    >
                                        <Typography fontSize="18px" sx={{ aspectRatio: "1 / 1", width: "25px" }} fontWeight="800"  >M</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Large">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontSize === 24 )}
                                        onClick={() => handleStrokeSettingChange( "fontSize", 24 )}
                                    >
                                        <Typography fontSize="18px" sx={{ aspectRatio: "1 / 1", width: "25px" }} fontWeight="800"  >L</Typography>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip placement="top" disableInteractive title="Extra large">
                                    <IconButton
                                        sx={getToogleBtnStyles( store.getBoardGlobalSettings.fontSize === 32 )}
                                        onClick={() => handleStrokeSettingChange( "fontSize", 32 )}
                                    >
                                        <Typography fontSize="18px" sx={{ aspectRatio: "1 / 1", width: "25px" }} fontWeight="800"  >XL</Typography>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>

            {/* === Tool Buttons === */}
            <IconButton
                ref={paletteBtnRef}
                sx={{
                    aspectRatio: "1 / 1",
                    borderRadius: "50%",
                    color: store.getBoardGlobalSettings.color?.hex,
                }}
                onClick={() => {
                    setStrokeOptionsOpen( true )
                }}
            >
                {Icons.default.Palette}
            </IconButton>

            <Tooltip placement="right" disableInteractive title="Select (V)">
                <IconButton
                    sx={getToogleBtnStyles( ["select"].includes( store.getTool ) )}
                    onClick={() => setTool( "select" )}
                >
                    {Icons.default.Select}
                </IconButton>
            </Tooltip>
            <Tooltip placement="right" disableInteractive title="Pan (H)">
                <IconButton
                    sx={getToogleBtnStyles( ["pan"].includes( store.getTool ) )}
                    onClick={() => setTool( "pan" )}
                >
                    {Icons.default.Pan}
                </IconButton>
            </Tooltip>

            <Tooltip placement="right" disableInteractive title="Pen (P)">
                <IconButton
                    sx={getToogleBtnStyles( ["pen"].includes( store.getTool ) )}
                    onClick={() => setTool( "pen" )}
                >
                    {Icons.default.Pen}
                </IconButton>
            </Tooltip>

            <Tooltip placement="right" disableInteractive title="Marker (M)">
                <IconButton
                    sx={getToogleBtnStyles( ["marker"].includes( store.getTool ) )}
                    onClick={() => setTool( "marker" )}
                >
                    {Icons.default.Highlighter}
                </IconButton>
            </Tooltip>

            <Tooltip placement="right" disableInteractive title="Eraser (E)">
                <IconButton
                    sx={getToogleBtnStyles( ["eraser"].includes( store.getTool ) )}
                    onClick={() => setTool( "eraser" )}
                >
                    {Icons.default.Eraser}
                </IconButton>
            </Tooltip>
            <Tooltip placement="right" disableInteractive title="Text (T)">
                <IconButton
                    sx={getToogleBtnStyles( ["text"].includes( store.getTool ) )}
                    onClick={() => setTool( "text" )}
                >
                    {Icons.default.Text}
                </IconButton>
            </Tooltip>


            <Tooltip placement="right" disableInteractive title="Rectangle (R)">
                <IconButton
                    sx={getToogleBtnStyles( ["rect"].includes( store.getTool ) )}
                    onClick={() => setTool( "rect" )}
                >
                    {Icons.default.Rect}
                </IconButton>
            </Tooltip>

            <Tooltip placement="right" disableInteractive title="Circlec (C)">
                <IconButton
                    sx={getToogleBtnStyles( ["circle"].includes( store.getTool ) )}
                    onClick={() => setTool( "circle" )}
                >
                    {Icons.default.Circle}
                </IconButton>
            </Tooltip>


            <Tooltip placement="right" disableInteractive title="Lock, stays in the same tool after adding element.">
                <IconButton
                    sx={{
                        aspectRatio: "1 / 1",
                        borderRadius: "5px",
                        fontSize: "18px",
                        bgcolor: store.getContainerState.lockToTool ? palette.secondary.main : "",
                        color: store.getContainerState.lockToTool ? "white" : palette.common.font,
                        border: store.getContainerState.lockToTool ? `1px solid #fff0` : border[1],
                        ":hover": { bgcolor: store.getContainerState.lockToTool ? palette.secondary.main : "" },
                    }}
                    onClick={() => store.setContainerState( { lockToTool: !store.getContainerState.lockToTool } )}
                >
                    {Icons.default.Lock}
                </IconButton>
            </Tooltip>
        </Box>
    )
} )

export default VirtualboardTools
